import { useSearchParams } from 'react-router-dom'
import { startCase } from 'lodash'
import { TasksComponent } from 'app/components/tasks/TasksComponent'
import { useTasks } from 'app/components/tasks/useTasks'
import { FilterObject, FilterValueType, PredefinedFilter, decodeFilters, encodeFilters } from 'app/filter.utils'
import { useStickyLocationDefault } from 'app/hooks/useStickyLocation'
import { observer } from 'mobx-react-lite'
import { useSavedSubpages } from 'app/hooks/useSavedSubpages'
import { useUser, useWorkspace } from 'app/utils'
import { myRoleId, myTasksSearch } from 'lib/taskAssignee'

export const TasksSlugs = [`open-tasks`, `completed-tasks`, `all-tasks`, `my-tasks`] as const

export default observer(function Tasks() {
  const [searchParams] = useSearchParams()
  const user = useUser()
  const workspace = useWorkspace()
  const stickyLocation = useStickyLocationDefault()

  if (!user) return <></>

  let tab: (typeof TasksSlugs)[number] | string =
    (searchParams.get(`tab`) as (typeof TasksSlugs)[number]) || `open-tasks`
  // `filters=` means the user cleared their own filters, and the panel is
  // right to show none. The tab's filter is not theirs to clear though:
  // Open Tasks without `status:open` is All Tasks. Read the value, not the key.
  let hasFilters = !!searchParams.get(`filters`)
  const groupBy = searchParams.get(`groupBy`)
  let joiner = searchParams.get(`join`) ?? `and`
  let urlFilters = searchParams.get(`filters`)
  let preAppliedFilters: PredefinedFilter = {}
  let baseSearch: string | undefined

  const { subpages } = useSavedSubpages(`tasks`)

  // Build filter config client-side (same logic from old loader)
  if (!hasFilters) {
    const subpage = subpages?.find((s) => s.id === tab)
    switch (tab) {
      case `open-tasks`:
        urlFilters = encodeFilters([
          {
            attribute: `status.keyword`,
            value: `open`,
            operator: `exactly`,
            displayName: `Status`,
            type: FilterValueType.string,
          },
        ])
        preAppliedFilters = { status: { value: `open`, operator: `exactly` } }
        break
      case `completed-tasks`:
        urlFilters = encodeFilters([
          {
            attribute: `status.keyword`,
            value: `closed`,
            operator: `exactly`,
            displayName: `Status`,
            type: FilterValueType.string,
          },
        ])
        preAppliedFilters = { status: { value: `closed`, operator: `exactly` } }
        break
      case `my-tasks`:
        baseSearch = myTasksSearch(user.id!, myRoleId(user, workspace?.id))
        break
      case `all-tasks`:
        // No pre-applied filter — show the full task list (open + closed),
        // ungrouped, sorted by date. This is the "raw" view: anything the
        // user has access to, no status filter.
        urlFilters = null
        preAppliedFilters = {}
        break
      default:
        if (subpage) {
          joiner = subpage.join
          urlFilters = encodeFilters(subpage.filters as FilterObject[])
          preAppliedFilters = subpage.filters.reduce(
            (acc, filter) => ({ ...acc, [filter.attribute]: { value: filter.value, operator: filter.operator } }),
            {},
          )
        }
        break
    }
  }

  // The per-device location rides along as one more tab default, as on
  // Contacts and Activities: the chip shows, and any later filter edit writes
  // it into the URL with the rest.
  if (!hasFilters && stickyLocation.length) {
    urlFilters = encodeFilters([
      ...decodeFilters(urlFilters),
      {
        attribute: `locationId`,
        value: stickyLocation,
        operator: `in`,
        displayName: `Location`,
        type: FilterValueType.id,
      },
    ])
    preAppliedFilters = { ...preAppliedFilters, locationId: { value: stickyLocation, operator: `in` } }
  }

  const data = {
    slug: tab ?? ``,
    // Flat shadcn-style table — always render as a single list. The legacy "due-date"
    // and "owner" groupings have been retired in favor of in-table sort + filters.
    groupBy: `none`,
    urlFilters,
    preAppliedFilters,
    initialFilters: subpages?.find((s) => s.id === tab)?.filters as FilterObject[] | undefined,
    subpages: subpages?.map((s) => s as Record<string, unknown>),
    joiner,
    baseSearch,
    activeSubpage: startCase(subpages?.find((s) => s.id === tab)?.name ?? tab),
  }

  const hookData = useTasks(data)
  return <TasksComponent {...hookData} />
})
